import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where as firestoreWhere,
  orderBy as firestoreOrderBy,
  limit as firestoreLimit,
  onSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import type { DocumentData } from 'firebase/firestore';
import { db } from '../config/firebase';

// Re-export db for other services to use
export { db };

// Firestore service class
export class FirestoreService {
  // Generic CRUD operations
  static async getDocument<T = DocumentData>(
    collectionName: string,
    documentId: string
  ): Promise<T | null> {
    try {
      const docRef = doc(db, collectionName, documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
      }
      return null;
    } catch (error) {
      throw new Error(`Failed to get document: ${error}`);
    }
  }

  static async getDocuments<T = DocumentData>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> {
    try {
      const collectionRef = collection(db, collectionName);
      const q = query(collectionRef, ...constraints);
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[];
    } catch (error) {
      throw new Error(`Failed to get documents: ${error}`);
    }
  }

  static async addDocument<T = DocumentData>(
    collectionName: string,
    data: T
  ): Promise<string> {
    try {
      const collectionRef = collection(db, collectionName);
      const docRef = await addDoc(collectionRef, data as DocumentData);
      return docRef.id;
    } catch (error) {
      throw new Error(`Failed to add document: ${error}`);
    }
  }

  static async updateDocument<T = DocumentData>(
    collectionName: string,
    documentId: string,
    data: Partial<T>
  ): Promise<void> {
    try {
      const docRef = doc(db, collectionName, documentId);
      await updateDoc(docRef, data as DocumentData);
    } catch (error) {
      throw new Error(`Failed to update document: ${error}`);
    }
  }

  static async deleteDocument(
    collectionName: string,
    documentId: string
  ): Promise<void> {
    try {
      const docRef = doc(db, collectionName, documentId);
      await deleteDoc(docRef);
    } catch (error) {
      throw new Error(`Failed to delete document: ${error}`);
    }
  }

  // Real-time listeners
  static onDocumentSnapshot<T = DocumentData>(
    collectionName: string,
    documentId: string,
    callback: (data: T | null) => void
  ): () => void {
    const docRef = doc(db, collectionName, documentId);
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() } as T);
      } else {
        callback(null);
      }
    });
  }

  static onCollectionSnapshot<T = DocumentData>(
    collectionName: string,
    constraints: QueryConstraint[] = [],
    callback: (data: T[]) => void
  ): () => void {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...constraints);
    return onSnapshot(q, (querySnapshot) => {
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[];
      callback(data);
    });
  }

  // Helper methods for common queries
  static async getDocumentsByField<T = DocumentData>(
    collectionName: string,
    field: string,
    value: any
  ): Promise<T[]> {
    return this.getDocuments<T>(collectionName, [firestoreWhere(field, '==', value)]);
  }

  static async getDocumentsOrdered<T = DocumentData>(
    collectionName: string,
    field: string,
    direction: 'asc' | 'desc' = 'asc',
    limitCount?: number
  ): Promise<T[]> {
    const constraints: QueryConstraint[] = [firestoreOrderBy(field, direction)];
    if (limitCount) {
      constraints.push(firestoreLimit(limitCount));
    }
    return this.getDocuments<T>(collectionName, constraints);
  }

  // Static methods for query constraints
  static orderBy(field: string, direction: 'asc' | 'desc' = 'asc') {
    return firestoreOrderBy(field, direction);
  }

  static where(field: string, operator: any, value: any) {
    return firestoreWhere(field, operator, value);
  }

  static limit(count: number) {
    return firestoreLimit(count);
  }
}

// Export individual functions for convenience
export const {
  getDocument,
  getDocuments,
  addDocument,
  updateDocument,
  deleteDocument,
  onDocumentSnapshot,
  onCollectionSnapshot,
  getDocumentsByField,
  getDocumentsOrdered,
  orderBy,
  where,
  limit,
} = FirestoreService; 